import { Link } from 'react-router-dom';
import { TERMS_COMPANY, TERMS_CONTACT_URL, TERMS_VERSION } from '@kortex/shared';
import { SiteFooter } from '../components/Layout';
import { isAndroidKiosk } from '../lib/androidBridge';

/**
 * The agreement a hotel enters at registration.
 *
 * Written against the law that actually governs a hospitality SaaS sold in
 * India - the Indian Contract Act, the IT Act and its rules, the DPDP Act, the
 * CGST Act's invoicing and record-keeping duties, the Consumer Protection
 * (E-Commerce) Rules and the RBI's payment aggregator directions - and kept in
 * the codebase rather than a CMS so that the version a hotel accepted can be
 * pinned to a commit.
 *
 * Bump TERMS_VERSION whenever a word of this changes: acceptance recorded
 * before a revision must never be mistaken for acceptance of what came after.
 */
export function Terms() {
  return (
    <div className="legal-shell">
      <header className="legal-head">
        <div className="brand-mark">S</div>
        <div>
          <h1>SARTHI — Terms and Conditions</h1>
          <div className="small">
            Version {TERMS_VERSION} · These terms apply to your use of SARTHI
          </div>
        </div>
        {/* In the Android app this is reached from the footer, signed in or not -
            the registration form is not where somebody came from. */}
        {isAndroidKiosk() ? (
          <Link className="btn sm" to="/">
            Back
          </Link>
        ) : (
          <Link className="btn sm" to="/register">
            Back to registration
          </Link>
        )}
      </header>

      <main className="legal-body">
        <p className="legal-lead">
          These terms are an electronic agreement under the Information Technology Act, 2000, and
          form a binding contract under the Indian Contract Act, 1872, between {TERMS_COMPANY}{' '}
          (“we”, “us”, “SARTHI”) and the hotel, restaurant or hospitality business that registers
          for the service (“you”, “the Hotel”). By ticking the acceptance box at registration, or by
          using the service, you agree to them. If you are accepting on behalf of a company, you
          confirm you are authorised to bind it.
        </p>

        <nav className="legal-toc" aria-label="Contents">
          <ol>
            <li>
              <a href="#definitions">Definitions</a>
            </li>
            <li>
              <a href="#service">The service</a>
            </li>
            <li>
              <a href="#eligibility">Eligibility and your account</a>
            </li>
            <li>
              <a href="#plans">Plans, trial, fees and taxes</a>
            </li>
            <li>
              <a href="#your-obligations">What you are responsible for</a>
            </li>
            <li>
              <a href="#tax">Invoicing, GST and statutory records</a>
            </li>
            <li>
              <a href="#guest-data">Guest data and privacy</a>
            </li>
            <li>
              <a href="#payments">Online payments and gateways</a>
            </li>
            <li>
              <a href="#acceptable-use">Acceptable use</a>
            </li>
            <li>
              <a href="#availability">Availability, support and backups</a>
            </li>
            <li>
              <a href="#ip">Intellectual property</a>
            </li>
            <li>
              <a href="#confidentiality">Confidentiality</a>
            </li>
            <li>
              <a href="#warranties">Warranties and disclaimers</a>
            </li>
            <li>
              <a href="#liability">Limitation of liability</a>
            </li>
            <li>
              <a href="#indemnity">Indemnity</a>
            </li>
            <li>
              <a href="#suspension">Suspension and termination</a>
            </li>
            <li>
              <a href="#exit">Getting your data out</a>
            </li>
            <li>
              <a href="#changes">Changes to these terms</a>
            </li>
            <li>
              <a href="#grievance">Grievances</a>
            </li>
            <li>
              <a href="#law">Governing law and disputes</a>
            </li>
            <li>
              <a href="#general">General</a>
            </li>
          </ol>
        </nav>

        <section id="definitions">
          <h2>1. Definitions</h2>
          <p>
            <b>Service</b> means the SARTHI software, its web application, its API and any
            documentation we provide. <b>Property</b> means a hotel or outlet you operate within the
            Service. <b>Your Data</b> means everything you or your staff put into the Service or
            generate through it — menus, recipes, stock, invoices, bookings, guest records, staff
            records and payroll. <b>Guest</b> means a person whose details you record in the
            Service.
            <b> Subscription</b> means the plan your account is on, including any free trial.
          </p>
        </section>

        <section id="service">
          <h2>2. The service</h2>
          <p>
            SARTHI is software for running a hotel and restaurant: point of sale, kitchen
            display, menu and recipe costing, inventory and procurement, front desk and
            housekeeping, reservations and online bookings, staff and payroll, reporting and GST
            extracts. We provide it as a subscription; we do not operate your hotel, prepare your
            food, take your bookings or file your returns.
          </p>
          <p>
            We may improve, change or discontinue features. Where a change materially reduces
            functionality you rely on, we will give you reasonable notice by email or in the
            application.
          </p>
        </section>

        <section id="eligibility">
          <h2>3. Eligibility and your account</h2>
          <ul>
            <li>
              You must be at least 18 years old and competent to contract under the Indian Contract
              Act, 1872.
            </li>
            <li>
              You must give accurate registration details. Your GSTIN, FSSAI licence number, legal
              name, address and PIN code appear on the tax invoices you issue through the Service —
              if they are wrong, the invoices are wrong, and that is your liability, not ours.
            </li>
            <li>
              You are responsible for every user you create, for the role you give them, and for
              everything done under their credentials. Keep passwords confidential and remove staff
              who leave.
            </li>
            <li>
              Tell us promptly if you believe an account has been compromised. Use the contact route
              in clause 19.
            </li>
          </ul>
        </section>

        <section id="plans">
          <h2>4. Plans, trial, fees and taxes</h2>
          <ul>
            <li>
              A free trial runs for the period stated on your plan at registration. No payment
              instrument is required to start one.
            </li>
            <li>
              Fees for paid plans are those published or agreed in writing when you subscribe. They
              are exclusive of GST and any other applicable tax, which is charged additionally at
              the prevailing rate.
            </li>
            <li>
              Subscription fees are payable in advance for the billing period. Except where the law
              requires otherwise, fees already paid are non-refundable, including where you stop
              using the Service part way through a period.
            </li>
            <li>
              Plan limits — the number of properties and users — are enforced by the Service. Moving
              between plans changes those limits from the moment the change takes effect.
            </li>
            <li>
              If a subscription lapses, the Service restricts writing while continuing to let you
              read and export your data for the period described in clause 17.
            </li>
            <li>
              We may revise fees on 30 days' written notice. A revision takes effect from your next
              billing period; you may cancel before it applies.
            </li>
          </ul>
        </section>

        <section id="your-obligations">
          <h2>5. What you are responsible for</h2>
          <p>
            You run a regulated business. The Service helps you record and calculate, but the
            obligations remain yours, including:
          </p>
          <ul>
            <li>
              holding and displaying a valid licence under the Food Safety and Standards Act, 2006,
              and complying with the conditions attached to it;
            </li>
            <li>
              registration and compliance under the Central Goods and Services Tax Act, 2017, the
              corresponding State Act, and the rules made under them;
            </li>
            <li>
              your licences and registrations under state Shops and Establishments legislation,
              excise law where you serve alcohol, local municipal law, and fire and police
              requirements for lodging;
            </li>
            <li>
              employment law, including minimum wages, provident fund, employees' state insurance,
              professional tax, and the accuracy of the attendance you record;
            </li>
            <li>
              the accuracy of what you enter: prices, tax rates, HSN and SAC codes, recipes, stock
              counts, tariffs and guest details.
            </li>
          </ul>
        </section>

        <section id="tax">
          <h2>6. Invoicing, GST and statutory records</h2>
          <p>
            The Service raises invoices in your name, computes GST from the rates and place of
            supply you configure, and produces GSTR-1 style extracts and e-invoice payloads. You
            remain the supplier for tax purposes. You must:
          </p>
          <ul>
            <li>
              check that the tax rate, HSN or SAC code and place of supply on each item are correct
              for your business before you sell;
            </li>
            <li>
              file your own returns, and reconcile what the Service reports against your books — our
              extracts are an aid to filing, not a filing;
            </li>
            <li>
              retain your accounts and records for the period required by section 36 of the CGST
              Act, 2017, which is seventy-two months from the due date of furnishing the annual
              return for the year concerned. Do not rely on the Service alone for that retention:
              export and keep your own copies.
            </li>
          </ul>
          <p>
            Room tariffs attract GST at the slab applicable to the tariff charged. The Service
            applies the slab configured for your rooms; confirming that it matches the law as it
            stands is your responsibility.
          </p>
        </section>

        <section id="guest-data">
          <h2>7. Guest data and privacy</h2>
          <p>
            Under the Digital Personal Data Protection Act, 2023, you are the Data Fiduciary for
            your guests' and staff's personal data. We process that data on your instructions as a
            Data Processor, for the purpose of providing the Service and for no other purpose. In
            practice:
          </p>
          <ul>
            <li>
              you decide what personal data to collect, for how long to keep it, and on what lawful
              basis — including the notice and consent your guests are given;
            </li>
            <li>
              we do not sell your data, do not use it to train models, and do not share it with
              anybody except as needed to run the Service or as the law requires;
            </li>
            <li>
              we apply reasonable security practices as contemplated by section 43A of the
              Information Technology Act, 2000, and the rules made under it: passwords are stored
              hashed, gateway and mail credentials are encrypted at rest, access is controlled by
              role, and one hotel's data is isolated from another's;
            </li>
            <li>
              if we become aware of a personal data breach affecting your data, we will notify you
              without undue delay with what we know, so that you can meet your own reporting duties
              to the Data Protection Board and to affected persons;
            </li>
            <li>
              where a guest exercises a right against you — access, correction, erasure — you can
              act on it within the Service; we will assist where you cannot;
            </li>
            <li>
              you must not put payment card numbers, CVVs, or more identity documentation than you
              actually need into free-text fields. The Service is not built to hold card data.
            </li>
          </ul>
          <p>
            Our own handling of the data you give us — your account details, your billing details —
            is described in our privacy notice, available from the contact page in clause 19.
          </p>
        </section>

        <section id="payments">
          <h2>8. Online payments and gateways</h2>
          <ul>
            <li>
              Where you enable online payments, you contract directly with the payment aggregator
              whose keys you configure. Their terms, their fees and their settlement timelines apply
              between you and them.
            </li>
            <li>
              Card details are entered in the gateway's own checkout and never reach SARTHI. We
              store only a reference to the payment and the gateway order it belongs to, and verify
              the signature the gateway returns.
            </li>
            <li>
              Payment aggregators operating in India do so under the Reserve Bank of India's
              directions on payment aggregators and payment gateways. Choosing an authorised one is
              your responsibility.
            </li>
            <li>
              Refunds, chargebacks and disputes with guests are between you, the guest and the
              aggregator. We can show you what the Service recorded; we cannot move money.
            </li>
            <li>
              If no provider is enabled, online bookings are held and the guest pays you at the
              hotel. Honouring a held booking is your commitment to the guest.
            </li>
          </ul>
        </section>

        <section id="acceptable-use">
          <h2>9. Acceptable use</h2>
          <p>You must not, and must not allow anyone to:</p>
          <ul>
            <li>
              use the Service to store or transmit anything unlawful, obscene, defamatory, or
              infringing, or anything listed as prohibited under rule 3 of the Information
              Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021;
            </li>
            <li>
              probe, scan or test the vulnerability of the Service, break or circumvent its
              authentication, or access data belonging to another hotel;
            </li>
            <li>
              resell, sublicense or white-label the Service, or use it to run a service bureau for
              businesses you do not own, without our written agreement;
            </li>
            <li>
              copy, decompile or reverse engineer the Service except to the extent section 52 of the
              Copyright Act, 1957 permits despite this restriction;
            </li>
            <li>
              use automated means to place load on the Service beyond ordinary use, or to scrape it;
            </li>
            <li>
              use the Service to send unsolicited commercial messages to guests in breach of
              applicable telecom or consumer law.
            </li>
          </ul>
        </section>

        <section id="availability">
          <h2>10. Availability, support and backups</h2>
          <ul>
            <li>
              We aim to keep the Service available at all times but do not guarantee uninterrupted
              operation. Planned maintenance is notified in advance where practicable.
            </li>
            <li>
              A specific uptime commitment applies only where we have agreed one with you in
              writing.
            </li>
            <li>
              Support is provided through the contact route in clause 19 during business hours,
              Monday to Saturday, excluding public holidays in Karnataka.
            </li>
            <li>
              Where SARTHI is deployed on your own hardware or your own hosting, availability,
              backups, patching and physical security of that environment are yours. Whoever
              operates the server operates it.
            </li>
            <li>
              We take backups of hosted environments, but you should export your own data
              periodically. Clause 17 explains how.
            </li>
          </ul>
        </section>

        <section id="ip">
          <h2>11. Intellectual property</h2>
          <p>
            The Service, its software, design and documentation belong to {TERMS_COMPANY}. You get a
            non-exclusive, non-transferable right to use it for your own hospitality business for as
            long as your subscription is in good standing, and nothing more.
          </p>
          <p>
            Your Data remains yours. You grant us only the licence needed to host, process, back up
            and display it so that we can provide the Service to you, and to produce aggregated
            statistics that do not identify you, your guests or your staff.
          </p>
          <p>
            Any feedback you give us may be used to improve the Service without obligation to you.
          </p>
        </section>

        <section id="confidentiality">
          <h2>12. Confidentiality</h2>
          <p>
            Each of us may learn things about the other that are not public — your trading figures,
            our non-public technical detail. Neither will disclose them except to people who need to
            know and are under a duty of confidence, or where the law or a court requires
            disclosure. This survives the end of the agreement.
          </p>
        </section>

        <section id="warranties">
          <h2>13. Warranties and disclaimers</h2>
          <p>
            We warrant that we will provide the Service with reasonable skill and care. Beyond that,
            and to the extent Indian law permits, the Service is provided “as is”: we do not warrant
            that it will be uninterrupted or error free, or that it will meet a requirement we have
            not agreed with you in writing.
          </p>
          <p>
            <b>Nothing in the Service is legal, tax or accounting advice.</b> Tax rates, slabs and
            filing formats change. The computations, reports and e-invoice payloads it produces are
            to be checked by you or your accountant before they are relied on or filed.
          </p>
          <p>
            Nothing in these terms excludes rights you have as a consumer under the Consumer
            Protection Act, 2019, where that Act applies to you.
          </p>
        </section>

        <section id="liability">
          <h2>14. Limitation of liability</h2>
          <ul>
            <li>
              Neither of us is liable for indirect or consequential loss, or for loss of profit,
              revenue, goodwill, anticipated savings or business opportunity.
            </li>
            <li>
              Our total liability arising out of or in connection with these terms in any twelve
              month period is limited to the subscription fees you actually paid us for the Service
              in that period.
            </li>
            <li>
              Where you use the Service on a free trial or a free plan, our total liability is
              limited to ten thousand rupees (₹10,000).
            </li>
            <li>
              Nothing here limits liability for fraud, for wilful misconduct, or for anything that
              cannot lawfully be limited.
            </li>
          </ul>
        </section>

        <section id="indemnity">
          <h2>15. Indemnity</h2>
          <p>
            You will indemnify us against claims, penalties and reasonable costs arising from your
            use of the Service in breach of these terms, from the content of Your Data, from your
            tax or licensing position, or from a claim by a guest or member of your staff relating
            to how you collected or used their personal data.
          </p>
        </section>

        <section id="suspension">
          <h2>16. Suspension and termination</h2>
          <ul>
            <li>
              You may stop using the Service at any time. Cancelling takes effect at the end of the
              current billing period.
            </li>
            <li>
              We may suspend access where fees are overdue, where use threatens the security or
              stability of the Service, or where we are required to by law. Except in an emergency
              we will tell you first and give you a chance to put it right.
            </li>
            <li>
              Either of us may terminate for material breach that is not remedied within 30 days of
              written notice.
            </li>
            <li>
              On termination your right to use the Service ends. Clause 17 governs what happens to
              your data.
            </li>
          </ul>
        </section>

        <section id="exit">
          <h2>17. Getting your data out</h2>
          <p>
            Your data is yours, and it is meant to leave with you. While your account is active you
            can export invoices, GST extracts and reports from the Service in CSV. After termination
            we will keep your data available for export for 30 days on request, and will then delete
            or irreversibly anonymise it, except where we are required to retain something by law.
          </p>
          <p>
            Because your statutory retention period under the CGST Act is far longer than that 30
            days, export before you leave.
          </p>
        </section>

        <section id="changes">
          <h2>18. Changes to these terms</h2>
          <p>
            We may revise these terms. The version is stated at the top of this page and recorded
            against your account when you accept. Where a revision materially affects your rights we
            will give notice by email or in the application, and continuing to use the Service after
            it takes effect is acceptance of the revision. If you do not accept it, you may
            terminate under clause 16.
          </p>
        </section>

        <section id="grievance">
          <h2>19. Grievances</h2>
          <p>
            In line with the Information Technology (Intermediary Guidelines and Digital Media
            Ethics Code) Rules, 2021 and the Consumer Protection (E-Commerce) Rules, 2020,
            complaints about the Service may be raised with our Grievance Officer, who will
            acknowledge within 48 hours and resolve within the period the applicable rules require.
          </p>
          <p>
            Grievance Officer, {TERMS_COMPANY} — reach us through{' '}
            <a href={TERMS_CONTACT_URL} target="_blank" rel="noopener noreferrer">
              europiumsolutions.com/contact-us
            </a>
            .
          </p>
        </section>

        <section id="law">
          <h2>20. Governing law and disputes</h2>
          <p>
            These terms are governed by the laws of India. We will first try to settle any dispute
            by discussion. Failing that, it will be referred to arbitration by a sole arbitrator
            under the Arbitration and Conciliation Act, 1996, seated at Bengaluru and conducted in
            English. Subject to that, the courts at Bengaluru, Karnataka have exclusive
            jurisdiction. Nothing prevents either of us from seeking urgent interim relief from a
            competent court.
          </p>
        </section>

        <section id="general">
          <h2>21. General</h2>
          <ul>
            <li>
              Neither of us is liable for failure caused by events beyond reasonable control,
              including network or power failure, natural disaster, epidemic, or government action.
            </li>
            <li>
              You may not assign these terms without our consent. We may assign them to a successor
              of our business.
            </li>
            <li>
              If a provision is held unenforceable, the rest stands and the provision is read down
              to what is enforceable.
            </li>
            <li>
              These terms, together with any written order or plan you subscribe to, are the whole
              agreement between us on this subject.
            </li>
            <li>
              Nothing here creates a partnership, joint venture or employment relationship between
              us.
            </li>
          </ul>
        </section>

        <p className="legal-foot">
          Version {TERMS_VERSION}. SARTHI is a product of {TERMS_COMPANY}.
        </p>
      </main>

      <SiteFooter />
    </div>
  );
}
